import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Thermometer, Droplets, Gauge, CloudRain, Sprout, Download } from 'lucide-react';
import { WeatherMetricCard } from './components/WeatherMetricCard';
import { WindRose } from './components/WindRose';
import { TemperatureChart } from './components/TemperatureChart';
import { HumidityChart } from './components/HumidityChart';
import { PressureChart } from './components/PressureChart';
import { WindChart } from './components/WindChart';
import { HourlyForecast } from './components/HourlyForecast';
import { RainGauge } from './components/RainGauge';
import { WeatherStats } from './components/WeatherStats';
import { AirQualityIndicator } from './components/AirQualityIndicator';
import { TimeRangeSelector } from './components/TimeRangeSelector';
import { ComparisonCard } from './components/ComparisonCard';
import { ToastProvider } from './components/ToastProvider';
import { Button } from './components/ui/button';
import {
  generateMockData,
  generateHourlyForecast,
  getCurrentWeather,
  type WeatherDataPoint,
} from './utils/mockWeatherData';
import { exportHistoricalData, exportCurrentData } from './utils/csvExport';
import { toast } from 'sonner';

// ============ helpers ============
const asArray = <T,>(v: T[] | T | null | undefined): T[] =>
  Array.isArray(v) ? v : v ? [v as any] : [];

const num = (v: any) => (v == null || v === '' ? 0 : Number(v));

/**
 * Zwraca wartość pola z próbki najbliższej tej samej porze sprzed 24 h.
 * Jeśli znajdzie próbkę w oknie ±30 min, wybiera najbliższą; w przeciwnym razie
 * bierze po prostu najbliższą w całym zbiorze (żeby jednak coś pokazać).
 */
const valueAtSameTimeYesterday = <T extends Record<string, any>>(
  rows: T[],
  field: keyof T,
  windowMinutes = 30
): number | null => {
  if (!rows?.length) return null;

  const target = Date.now() - 24 * 60 * 60 * 1000; // teraz - 24h
  const windowMs = windowMinutes * 60 * 1000;

  let bestAnyVal: number | null = null;
  let bestAnyDiff = Infinity;

  let bestInWindowVal: number | null = null;
  let bestInWindowDiff = Infinity;

  for (const d of rows) {
    const ms = new Date((d as any).time).getTime();
    const v = Number((d as any)[field]);
    if (!Number.isFinite(v)) continue;

    const diff = Math.abs(ms - target);

    // do statystyk "dowolnie najbliższa"
    if (diff < bestAnyDiff) {
      bestAnyDiff = diff;
      bestAnyVal = v;
    }

    // jeśli mieści się w oknie – preferujemy to dopasowanie
    if (diff <= windowMs && diff < bestInWindowDiff) {
      bestInWindowDiff = diff;
      bestInWindowVal = v;
    }
  }

  return bestInWindowVal ?? bestAnyVal;
};
// =================================
// korekta ciśnienia do poziomu morza
const seaLevelPressure = (pressure: number, altitudeMeters: number): number => {
  if (!pressure || pressure <= 0) return 0;
  return pressure * Math.pow(1 - (altitudeMeters / 44330), -5.255);
};

export default function App() {
  // stan
  const [currentData, setCurrentData] = useState<WeatherDataPoint | null>(null);
  const [timeRange, setTimeRange] = useState(24);
  const [historicalData, setHistoricalData] = useState<WeatherDataPoint[]>([]);
  const [forecast, setForecast] = useState(generateHourlyForecast());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // suma opadów z 7 dni
  const [weekRain, setWeekRain] = useState(0);

  // tytuł strony
  useEffect(() => {
    document.title = 'Stacja Pogodowa Arctus2';
  }, []);

  // pierwszy fetch
  useEffect(() => {
    (async () => {
      try {
        const [hist, curr] = await Promise.all([
          generateMockData(timeRange),
          getCurrentWeather(),
        ]);
        setHistoricalData(Array.isArray(hist) ? hist : []);
        setCurrentData(curr ?? null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // aktualizuj historię przy zmianie zakresu
  useEffect(() => {
    (async () => {
      const hist = await generateMockData(timeRange);
      setHistoricalData(Array.isArray(hist) ? hist : []);
    })();
  }, [timeRange]);

  // real-time – co 5 s dociągaj bieżące dane
  useEffect(() => {
    const id = setInterval(async () => {
      const newData = await getCurrentWeather();
      if (newData) {
        setCurrentData(newData);
        setLastUpdate(new Date());
      }
    }, 5000);
    return () => clearInterval(id);
  }, []);

  // policz tydzień niezależnie od wybranego zakresu (7*24h)
  useEffect(() => {
    (async () => {
      const last7days = await generateMockData(24 * 7);
      const sumWeek = last7days.reduce((sum, d) => sum + num(d.rain_mm), 0);
      setWeekRain(sumWeek);
    })();
    // jeśli chcesz odświeżać wraz z bieżącą aktualizacją, podmień [] na [lastUpdate]
  }, []);

  // BEZPIECZNE źródło do obliczeń
  const rows = asArray(historicalData);

  // sumy opadów – DZIŚ od 00:00 lokalnego czasu
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const rainToday = rows
    .filter((d) => new Date(d.time).getTime() >= startOfToday.getTime())
    .reduce((sum, d) => sum + num(d.rain_mm), 0);

  // wartości "wczoraj o tej porze" (z tolerancją ±30 min)
  const sameYesterday = {
    temp:     valueAtSameTimeYesterday(rows, 'aht_temp'),
    humidity: valueAtSameTimeYesterday(rows, 'aht_hum'),
    pressure: valueAtSameTimeYesterday(rows, 'bmp_pres'),
    wind:     valueAtSameTimeYesterday(rows, 'wind_speed'),
  };

  if (loading && !currentData && rows.length === 0) {
    return (
      <>
        <ToastProvider />
        <div className="min-h-screen flex items-center justify-center text-gray-500">
          Ładowanie danych…
        </div>
      </>
    );
  }

  // fallback na wypadek braku currentData
  const cur =
    currentData ??
    ({
      time: new Date().toISOString(),
      aht_temp: 0,
      aht_hum: 0,
      bmp_temp: 0,
      bmp_pres: 0,
      ds18_temp: 0,
      wind_speed: 0,
      wind_gust: 0,
      rain_mm: 0,
      wind_dir_val: 0,
      soil_moisture: 0,
    } as WeatherDataPoint);

  // ===== chart-safe datasets (same numbers, bez null) =====
  const tempData = rows.map((d) => ({
    time: d.time,
    aht_temp: num(d.aht_temp),
    bmp_temp: num(d.bmp_temp),
    ds18_temp: num(d.ds18_temp),
  }));

  const humData = rows.map((d) => ({
    time: d.time,
    aht_hum: num(d.aht_hum),
    soil_moisture: num(d.soil_moisture),
  }));

  const pressureData = rows.map((d) => ({
    time: d.time,
    bmp_pres: num(d.bmp_pres),
  }));

  const windData = rows.map((d) => ({
    time: d.time,
    wind_speed: num(d.wind_speed),
    wind_gust: num(d.wind_gust),
  }));
  // ========================================================

  return (
    <>
      <ToastProvider />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-[1600px] mx-auto mb-8"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1>Stacja Pogodowa Arctus2</h1>
              </div>
              <motion.p
                className="text-gray-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                System monitoringu pogody w czasie rzeczywistym
              </motion.p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  onClick={() => {
                    exportCurrentData(cur);
                    toast.success('Aktualne dane wyeksportowane do CSV');
                  }}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Eksportuj aktualne
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Button
                  onClick={() => {
                    exportHistoricalData(rows);
                    toast.success('Dane historyczne wyeksportowane do CSV');
                  }}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Eksportuj historię
                </Button>
              </motion.div>

              <motion.div
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-gray-600">
                  Ostatnia aktualizacja: {lastUpdate.toLocaleTimeString('pl-PL')}
                </span>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <div className="max-w-[1600px] mx-auto space-y-8">
          {/* Time range + AQI */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TimeRangeSelector selected={timeRange} onChange={setTimeRange} />
            <div className="flex-1 max-w-md">
              <AirQualityIndicator
                temperature={num(cur.aht_temp)}
                humidity={num(cur.aht_hum)}
                pressure={num(cur.bmp_pres)}
              />
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <WeatherMetricCard
              title="Temperatura (AHT)"
              value={num(cur.aht_temp)}
              unit="°C"
              icon={Thermometer}
              trend="up"
              color="bg-red-500"
              delay={0}
            />
            <WeatherMetricCard
              title="Temperatura (BMP)"
              value={num(cur.bmp_temp)}
              unit="°C"
              icon={Thermometer}
              trend="stable"
              color="bg-orange-500"
              delay={0.1}
            />
            <WeatherMetricCard
              title="Wilgotność"
              value={num(cur.aht_hum)}
              unit="%"
              icon={Droplets}
              trend="down"
              color="bg-blue-500"
              delay={0.2}
            />
            <WeatherMetricCard
              title="Ciśnienie"
              value={num(cur.bmp_pres)}
              unit="hPa"
              icon={Gauge}
              trend="up"
              color="bg-purple-500"
              delay={0.3}
            />
            <WeatherMetricCard
              title="Ciśnienie 170m n.p.m."
              value={seaLevelPressure(num(cur.bmp_pres), 170).toFixed(2)}
              unit="hPa"
              icon={Gauge}
              trend="stable"
              color="bg-green-500"
              delay={0.5}
            />
            <WeatherMetricCard
              title="Opady"
              value={num(cur.rain_mm)}
              unit="mm"
              icon={CloudRain}
              trend="stable"
              color="bg-cyan-500"
              delay={0.4}
            />
            
          </div>

          {/* Wind + Rain */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WindRose
              direction={num(cur.wind_dir_val)}
              speed={num(cur.wind_speed)}
              gust={num(cur.wind_gust)}
            />
            <RainGauge
              current={num(cur.rain_mm)}
              today={num(rainToday)}
              week={num(weekRain)}
            />
          </div>

          {/* Forecast */}
          <HourlyForecast forecast={forecast} />

          {/* Stats */}
          <WeatherStats data={rows} />

          {/* Comparison with yesterday (same-time) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h3 className="mb-4">Porównanie z wczoraj o tej porze</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ComparisonCard
                current={num(cur.aht_temp)}
                yesterday={num(sameYesterday.temp)}
                label="Temperatura"
                unit="°C"
              />
              <ComparisonCard
                current={num(cur.aht_hum)}
                yesterday={num(sameYesterday.humidity)}
                label="Wilgotność"
                unit="%"
              />
              <ComparisonCard
                current={num(cur.bmp_pres)}
                yesterday={num(sameYesterday.pressure)}
                label="Ciśnienie"
                unit="hPa"
              />
              <ComparisonCard
                current={num(cur.wind_speed)}
                yesterday={num(sameYesterday.wind)}
                label="Wiatr"
                unit="km/h"
              />
            </div>
          </motion.div>

          {/* Charts */}
          <div className="space-y-6">
            <TemperatureChart data={tempData} />
            <HumidityChart data={humData} />
            <PressureChart data={pressureData} />
            <WindChart data={windData} />
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="text-center text-gray-500 text-sm py-8 border-t border-gray-200 mt-8"
          >
            <p className="text-gray-400">Copyright © 2025 Pietech</p>
          </motion.div>
        </div>
      </div>
    </>
  );
}
